import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, Package, Zap, Clock, Check, X, Loader2, FileText, AlertTriangle, BarChart3, Archive, RefreshCw, Sparkles, ShoppingBag, Tag, TrendingDown, FileCheck } from 'lucide-react';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { API, useAuth, useLanguage, useCurrency } from '../App';
import { getRentalPeriodLabel } from '../utils/currencyFormatter';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { DashboardContainer, PageHeader, FilterChips } from '../components/DashboardLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ComingSoonOverlay, useBoostConfig } from '../components/BoostComponents';
import PriceReductionModal from '../components/modals/PriceReductionModal';

const SENTOO_LOGO_BADGE = 'https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/swh8n2bn_badge-bdc-mcb-ob-gradient-small.png';
const SENTOO_LOGO_ICON = 'https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/7ho9siu9_icon-checkout-full-2x.png';

// Boost modal translations
const boostTranslations = {
  en: {
    boost_your_listing: 'Boost your listing',
    get_more_visibility: 'Get more visibility',
    coming_soon: 'Coming Soon',
    sentoo_integration: 'Sentoo payment integration',
    cancel: 'Cancel',
    hours: 'hours',
    days: 'days',
    payment_coming_soon: 'Payment feature coming soon. We are integrating with Sentoo for secure payments.',
    boost_requested: 'Boost requested!',
    boost_not_available: 'Boost feature is currently not available',
    boost_error: 'Something went wrong while boosting',
  },
  nl: {
    boost_your_listing: 'Boost je advertentie',
    get_more_visibility: 'Krijg meer zichtbaarheid',
    coming_soon: 'Coming Soon',
    sentoo_integration: 'Sentoo betaalintegratie',
    cancel: 'Annuleren',
    hours: 'uur',
    days: 'dagen',
    payment_coming_soon: 'Betaalfunctie komt binnenkort beschikbaar. We integreren met Sentoo voor veilige betalingen.',
    boost_requested: 'Boost aangevraagd!',
    boost_not_available: 'Boost functie is momenteel niet beschikbaar',
    boost_error: 'Er ging iets mis bij het boosten',
  },
  es: {
    boost_your_listing: 'Impulsa tu anuncio',
    get_more_visibility: 'Obtén más visibilidad',
    coming_soon: 'Próximamente',
    sentoo_integration: 'Integración de pago Sentoo',
    cancel: 'Cancelar',
    hours: 'horas',
    days: 'días',
    payment_coming_soon: 'Función de pago próximamente. Estamos integrando Sentoo para pagos seguros.',
    boost_requested: '¡Impulso solicitado!',
    boost_not_available: 'La función de impulso no está disponible actualmente',
    boost_error: 'Algo salió mal al impulsar',
  }
};

const MyListingsPage = () => {
  const { token, fetchUser } = useAuth();
  const { t, language } = useLanguage();
  const { formatPrice } = useCurrency();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const boostConfig = useBoostConfig();
  
  // Helper for boost translations
  const tb = (key) => boostTranslations[language]?.[key] || boostTranslations.en[key] || key;
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Draft state
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [deletingDraftId, setDeletingDraftId] = useState(null);
  const [showDeleteDraftDialog, setShowDeleteDraftDialog] = useState(null);
  
  // Promotion state
  const [promotionPackages, setPromotionPackages] = useState([]);
  const [showPromotionModal, setShowPromotionModal] = useState(null);
  const [promoting, setPromoting] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [extendingId, setExtendingId] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Management modal state
  const [manageListing, setManageListing] = useState(null); // The listing being managed
  const [markingStatus, setMarkingStatus] = useState(null); // { id, action }
  const [showPriceReductionModal, setShowPriceReductionModal] = useState(false);
  const [priceReductionListing, setPriceReductionListing] = useState(null);
  const [reducingPrice, setReducingPrice] = useState(false);
  
  // Quick action handlers for mark sold/reserved
  const handleMarkSold = async (listingId) => {
    setMarkingStatus({ id: listingId, action: 'sold' });
    try {
      await axios.post(`${API}/listings/${listingId}/mark-sold`);
      toast.success(language === 'nl' ? 'Advertentie gemarkeerd als verkocht!' : 'Listing marked as sold!');
      setListings(prev => prev.map(l => 
        l.id === listingId ? { ...l, status: 'sold' } : l
      ));
      setManageListing(null); // Close modal
    } catch (error) {
      toast.error(language === 'nl' ? 'Kon status niet wijzigen' : 'Failed to update status');
    } finally {
      setMarkingStatus(null);
    }
  };

  const handleMarkReserved = async (listingId) => {
    setMarkingStatus({ id: listingId, action: 'reserved' });
    try {
      await axios.post(`${API}/listings/${listingId}/mark-reserved`);
      toast.success(language === 'nl' ? 'Advertentie gemarkeerd als gereserveerd!' : 'Listing marked as reserved!');
      setListings(prev => prev.map(l => 
        l.id === listingId ? { ...l, status: 'reserved' } : l
      ));
      setManageListing(null); // Close modal
    } catch (error) {
      toast.error(language === 'nl' ? 'Kon status niet wijzigen' : 'Failed to update status');
    } finally {
      setMarkingStatus(null);
    }
  };

  const handleUnmarkSold = async (listingId) => {
    setMarkingStatus({ id: listingId, action: 'unsold' });
    try {
      await axios.post(`${API}/listings/${listingId}/unmark-sold`);
      toast.success(language === 'nl' ? 'Advertentie geheractiveerd!' : 'Listing reactivated!');
      setListings(prev => prev.map(l => 
        l.id === listingId ? { ...l, status: 'active' } : l
      ));
      setManageListing(null); // Close modal
    } catch (error) {
      toast.error(language === 'nl' ? 'Kon status niet wijzigen' : 'Failed to update status');
    } finally {
      setMarkingStatus(null);
    }
  };

  const handleUnmarkReserved = async (listingId) => {
    setMarkingStatus({ id: listingId, action: 'unreserved' });
    try {
      await axios.post(`${API}/listings/${listingId}/unmark-reserved`);
      toast.success(language === 'nl' ? 'Reservering verwijderd!' : 'Reservation removed!');
      setListings(prev => prev.map(l => 
        l.id === listingId ? { ...l, status: 'active' } : l
      ));
      setManageListing(null); // Close modal
    } catch (error) {
      toast.error(language === 'nl' ? 'Kon status niet wijzigen' : 'Failed to update status');
    } finally {
      setMarkingStatus(null);
    }
  };

  // Handler for "Under Offer" status
  const handleMarkUnderOffer = async (listingId) => {
    setMarkingStatus({ id: listingId, action: 'under_offer' });
    try {
      await axios.patch(`${API}/listings/${listingId}/status`, { status: 'under_offer' });
      toast.success(language === 'nl' ? 'Advertentie gemarkeerd als "Onder Bod"!' : 'Listing marked as "Under Offer"!');
      setListings(prev => prev.map(l => 
        l.id === listingId ? { ...l, status: 'under_offer' } : l
      ));
      setManageListing(null);
    } catch (error) {
      toast.error(language === 'nl' ? 'Kon status niet wijzigen' : 'Failed to update status');
    } finally {
      setMarkingStatus(null);
    }
  };

  // Handler for removing "Under Offer" status
  const handleUnmarkUnderOffer = async (listingId) => {
    setMarkingStatus({ id: listingId, action: 'un_under_offer' });
    try {
      await axios.patch(`${API}/listings/${listingId}/status`, { status: 'available' });
      toast.success(language === 'nl' ? 'Onder Bod status verwijderd!' : 'Under Offer status removed!');
      setListings(prev => prev.map(l => 
        l.id === listingId ? { ...l, status: 'active' } : l
      ));
      setManageListing(null);
    } catch (error) {
      toast.error(language === 'nl' ? 'Kon status niet wijzigen' : 'Failed to update status');
    } finally {
      setMarkingStatus(null);
    }
  };

  // Open price reduction modal
  const handleOpenPriceReduction = (listing) => {
    setPriceReductionListing(listing);
    setShowPriceReductionModal(true);
  };

  // Handle price reduction confirmation
  const handleConfirmPriceReduction = async (newPrice) => {
    if (!priceReductionListing) return;
    
    setReducingPrice(true);
    try {
      const response = await axios.patch(`${API}/listings/${priceReductionListing.id}/reduce-price`, {
        new_price: newPrice
      });
      
      toast.success(
        language === 'nl' 
          ? `Prijs verlaagd naar ${response.data.currency} ${newPrice.toLocaleString()}!`
          : `Price reduced to ${response.data.currency} ${newPrice.toLocaleString()}!`
      );
      
      // Update local state with new pricing
      setListings(prev => prev.map(l => 
        l.id === priceReductionListing.id 
          ? { 
              ...l, 
              pricing: { 
                ...l.pricing, 
                amount: newPrice, 
                original_price: response.data.original_price 
              } 
            } 
          : l
      ));
      
      setShowPriceReductionModal(false);
      setPriceReductionListing(null);
      setManageListing(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to reduce price';
      toast.error(language === 'nl' ? `Kon prijs niet verlagen: ${errorMsg}` : errorMsg);
    } finally {
      setReducingPrice(false);
    }
  };

  const handleExtendListing = async (listingId) => {
    setExtendingId(listingId);
    try {
      const response = await axios.post(`${API}/listings/${listingId}/extend`);
      
      if (response.data.success) {
        // Show confetti celebration
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
        
        toast.success(
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <span>{response.data.message}</span>
          </div>,
          { duration: 5000 }
        );
        
        // Refresh listings
        fetchListings();
      }
    } catch (error) {
      console.error('Error extending listing:', error);
      toast.error(error.response?.data?.detail || 'Kon advertentie niet verlengen');
    } finally {
      setExtendingId(null);
    }
  };
  
  // Filtered listings based on status
  const getStatusCounts = () => {
    const active = listings.filter(l => l.status === 'active').length;
    const reserved = listings.filter(l => l.status === 'reserved').length;
    const pending = listings.filter(l => l.status === 'pending').length;
    const sold = listings.filter(l => l.status === 'sold').length;
    const expired = listings.filter(l => l.status === 'expired').length;
    const archived = listings.filter(l => l.status === 'archived').length;
    return { active, reserved, pending, sold, expired, archived };
  };
  
  const filteredListings = statusFilter === 'all' 
    ? listings 
    : statusFilter === 'active' 
      ? listings.filter(l => l.status === 'active')
      : statusFilter === 'reserved' 
        ? listings.filter(l => l.status === 'reserved')
        : statusFilter === 'pending' 
          ? listings.filter(l => l.status === 'pending')
          : statusFilter === 'sold' 
            ? listings.filter(l => l.status === 'sold')
            : statusFilter === 'expired'
              ? listings.filter(l => l.status === 'expired')
              : listings.filter(l => l.status === 'archived');

  useEffect(() => {
    fetchListings();
    fetchDrafts();
    fetchPromotionPackages();
    
    // Check if returning from payment
    const sessionId = searchParams.get('session_id');
    const promotionStatus = searchParams.get('promotion');
    
    if (sessionId && promotionStatus === 'success') {
      checkPromotionPayment(sessionId);
    } else if (promotionStatus === 'cancelled') {
      toast.error('Promotion payment was cancelled');
    }
  }, [page, searchParams]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/listings/my?page=${page}&limit=12`);
      setListings(response.data.listings);
      setTotal(response.data.total);
      setPages(response.data.pages);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const response = await axios.get(`${API}/drafts`);
      setDrafts(response.data);
    } catch (error) {
      console.error('Failed to fetch drafts:', error);
    } finally {
      setLoadingDrafts(false);
    }
  };

  const deleteDraft = async (draftId) => {
    setDeletingDraftId(draftId);
    try {
      await axios.delete(`${API}/drafts/${draftId}`);
      setDrafts(drafts.filter(d => d.id !== draftId));
      toast.success('Draft deleted');
      setShowDeleteDraftDialog(null);
    } catch (error) {
      console.error('Failed to delete draft:', error);
      toast.error('Failed to delete draft');
    } finally {
      setDeletingDraftId(null);
    }
  };

  const fetchPromotionPackages = async () => {
    try {
      const response = await axios.get(`${API}/promotions/packages?lang=${language}`);
      setPromotionPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch promotion packages:', error);
    }
  };

  const checkPromotionPayment = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/promotions/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          toast.success('Your listing has been promoted!');
          fetchListings();
          setCheckingPayment(false);
          // Clean URL
          window.history.replaceState({}, '', '/my-listings');
          return;
        } else if (response.data.status === 'expired') {
          toast.error('Payment session expired');
          setCheckingPayment(false);
          window.history.replaceState({}, '', '/my-listings');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.info('Payment is being processed. Check back soon.');
          setCheckingPayment(false);
          window.history.replaceState({}, '', '/my-listings');
        }
      } catch (error) {
        console.error('Payment check error:', error);
        setCheckingPayment(false);
      }
    };
    
    poll();
  };

  const handlePromote = async (listingId, packageId) => {
    setPromoting(true);
    try {
      const response = await axios.post(
        `${API}/promotions/checkout`,
        {
          listing_id: listingId,
          package_id: packageId,
          origin_url: window.location.origin,
        });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to start promotion checkout');
      setPromoting(false);
    }
  };

  const handleBoost = async (listingId, packageId) => {
    setPromoting(true);
    try {
      const response = await axios.post(
        `${API}/payments/boost/create`,
        null,
        { params: { listing_id: listingId, package_id: packageId } }
      );
      
      if (response.data.success) {
        // Redirect to Sentoo payment page
        if (response.data.payment_url) {
          window.location.href = response.data.payment_url;
        } else {
          toast.success(tb('boost_requested'));
          setShowPromotionModal(null);
        }
      } else {
        // Show translated message based on status
        const message = response.data.status === 'not_configured' 
          ? tb('payment_coming_soon')
          : tb('boost_not_available');
        toast.info(message);
        setPromoting(false);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || tb('boost_error');
      toast.error(errorMessage);
      setPromoting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/listings/${deleteId}`);
      toast.success('Listing deleted');
      setDeleteId(null);
      fetchListings();
      fetchUser();
    } catch (error) {
      toast.error('Failed to delete listing');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getPromotionTimeLeft = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    
    if (diff <= 0) return null;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h left`;
    return `${hours}h left`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="my-listings-page">
      <Navbar />

      {/* Payment checking overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[150] flex items-center justify-center">
          <div className="bg-card p-8 rounded-2xl shadow-lg text-center">
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Confirming Payment...</h3>
            <p className="text-muted-foreground">Please wait while we verify your payment.</p>
          </div>
        </div>
      )}

      {/* Confetti Animation Overlay */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-[150] overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-10px`,
              }}
            >
              <div
                className={`w-3 h-3 rotate-45 ${
                  ['bg-amber-400', 'bg-orange-400', 'bg-green-400', 'bg-blue-400', 'bg-purple-400', 'bg-pink-400'][
                    Math.floor(Math.random() * 6)
                  ]
                }`}
                style={{
                  animation: `fall ${2 + Math.random() * 2}s linear forwards`,
                  animationDelay: `${Math.random() * 0.5}s`,
                }}
              />
            </div>
          ))}
          <style>{`
            @keyframes fall {
              0% { transform: translateY(0) rotate(0deg); opacity: 1; }
              100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
            }
          `}</style>
        </div>
      )}

      <DashboardContainer>
        {/* Standardized Header */}
        <PageHeader pageKey="my-listings">
          <Link
            to="/seller-analytics"
            className="inline-flex items-center justify-center gap-2 p-2.5 sm:px-4 sm:py-2.5 bg-secondary text-foreground rounded-full font-medium hover:bg-secondary/80 transition-colors"
            data-testid="seller-analytics-btn"
          >
            <BarChart3 className="w-5 h-5" />
            <span className="hidden sm:inline">Analytics</span>
          </Link>
          <Link
            to="/create-listing"
            className="inline-flex items-center justify-center gap-2 p-2.5 sm:px-5 sm:py-2.5 bg-primary text-primary-foreground rounded-full font-medium hover:opacity-90 transition-opacity btn-press"
            data-testid="create-new-listing-btn"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">New Listing</span>
          </Link>
        </PageHeader>

        {/* Filter Chips */}
        <FilterChips
          options={[
            { key: 'all', label: 'All', icon: Package, count: loading ? undefined : listings.length },
            { key: 'active', label: 'Active', icon: Check, count: loading ? undefined : getStatusCounts().active },
            { key: 'reserved', label: 'Reserved', icon: Tag, count: loading ? undefined : getStatusCounts().reserved },
            { key: 'pending', label: 'Pending', icon: Clock, count: loading ? undefined : getStatusCounts().pending },
            { key: 'sold', label: 'Sold', icon: ShoppingBag, count: loading ? undefined : getStatusCounts().sold },
            { key: 'expired', label: 'Verlopen', icon: AlertTriangle, count: loading ? undefined : getStatusCounts().expired },
            { key: 'archived', label: 'Gearchiveerd', icon: Archive, count: loading ? undefined : getStatusCounts().archived }
          ]}
          activeFilter={statusFilter}
          onFilterChange={setStatusFilter}
        />

        {/* Promotion Info Banner - Coming Soon */}
        <ComingSoonOverlay
          title="Coming Soon"
          subtitle="Boost met Sentoo"
          icon={Zap}
          useBoostEnabled={true}
          className="mb-8"
        >
          <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">Boost je advertenties</h3>
                <p className="text-sm text-muted-foreground">
                  Krijg meer zichtbaarheid! Zet je advertentie bovenaan vanaf XCG {boostConfig.packages?.[0]?.price?.toFixed(2) || '1.75'}
                </p>
              </div>
              <img 
                src={SENTOO_LOGO_ICON} 
                alt="Sentoo" 
                className="h-8 object-contain opacity-60"
              />
            </div>
          </div>
        </ComingSoonOverlay>

        {/* Draft Listings Section */}
        {!loadingDrafts && drafts.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              Draft Listings
              <span className="text-sm font-normal text-muted-foreground">({drafts.length})</span>
            </h2>
            <div className="bg-amber-500/5 border-2 border-dashed border-amber-500/30 rounded-2xl p-4">
              <div className="space-y-3">
                {drafts.map((draft) => (
                  <div
                    key={draft.id}
                    className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border hover:border-amber-500/50 transition-colors"
                  >
                    {/* Draft preview image or placeholder */}
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0 flex items-center justify-center">
                      {draft.images && draft.images.length > 0 ? (
                        <img 
                          src={draft.images[0]} 
                          alt="Draft" 
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      ) : (
                        <FileText className="w-6 h-6 text-muted-foreground" />
                      )}
                    </div>
                    
                    {/* Draft info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">
                        {draft.title || 'Untitled Draft'}
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        {draft.category && (
                          <span className="text-xs px-2 py-0.5 bg-secondary rounded">
                            {draft.category}
                          </span>
                        )}
                        <span className="text-xs">
                          Last edited: {new Date(draft.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Link
                        to="/create-listing"
                        className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
                      >
                        <Edit2 className="w-4 h-4" />
                        Continue Editing
                      </Link>
                      <button
                        type="button"
                        onClick={() => setShowDeleteDraftDialog(draft.id)}
                        disabled={deletingDraftId === draft.id}
                        className="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Delete draft"
                      >
                        {deletingDraftId === draft.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-muted-foreground text-center">
                These are unfinished listings. Click "Continue Editing" to complete and publish them.
              </p>
            </div>

            {/* Delete Draft Confirmation Dialog */}
            <Dialog open={!!showDeleteDraftDialog} onOpenChange={() => setShowDeleteDraftDialog(null)}>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    Delete Draft?
                  </DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete this draft? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowDeleteDraftDialog(null)}
                    className="flex-1 px-4 py-2.5 bg-secondary text-foreground rounded-lg font-medium hover:bg-secondary/80 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteDraft(showDeleteDraftDialog)}
                    disabled={deletingDraftId}
                    className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                  >
                    {deletingDraftId ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Delete
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {loading ? (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-card rounded-2xl overflow-hidden border border-border">
                <div className="aspect-[4/3] skeleton"></div>
                <div className="p-4 space-y-3">
                  <div className="h-4 skeleton rounded w-3/4"></div>
                  <div className="h-6 skeleton rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredListings.length > 0 ? (
          <>
            <div className="mt-6 bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
              {/* Table Header - Desktop */}
              <div className="hidden md:grid md:grid-cols-12 gap-4 p-4 bg-muted/50 border-b border-border text-sm font-medium text-muted-foreground">
                <div className="col-span-4">Listing</div>
                <div className="col-span-2">Price</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Views</div>
                <div className="col-span-2">Actions</div>
              </div>

              {/* Listings */}
              <div className="divide-y divide-border">
                {filteredListings.map((listing) => {
                  const promotionTimeLeft = listing.promotion_expires ? getPromotionTimeLeft(listing.promotion_expires) : null;
                  const isPromoted = listing.is_promoted && promotionTimeLeft;
                  
                  return (
                    <div
                      key={listing.id}
                      className={`p-4 hover:bg-muted/30 transition-colors ${isPromoted ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}
                      data-testid={`listing-row-${listing.id}`}
                    >
                      {/* Mobile Layout */}
                      <div className="md:hidden">
                        <div className="flex gap-4">
                          <div className="relative w-20 h-20 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                            {listing.images?.[0] ? (
                              <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-8 h-8 text-muted-foreground" />
                              </div>
                            )}
                            {isPromoted && (
                              <div className="absolute top-1 left-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                <Zap className="w-2.5 h-2.5" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <Link to={`/listing/${listing.id}`} className="font-medium text-foreground hover:text-primary line-clamp-1">
                              {listing.title}
                            </Link>
                            <p className="text-primary font-semibold">
                              {listing.listing_type === 'yard_sale' && listing.yard_sale?.items?.length > 0 ? (
                                // Calculate total price for yard sale (including quantities)
                                (() => {
                                  const totalPrice = listing.yard_sale.items.reduce((sum, item) => 
                                    sum + ((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1)), 0
                                  );
                                  const currency = listing.yard_sale?.currency || listing.pricing?.currency || 'USD';
                                  return formatPrice(totalPrice, currency);
                                })()
                              ) : (
                                formatPrice(listing.pricing?.amount || 0, listing.pricing?.currency)
                              )}
                              {/* Price unit suffix (e.g., /m²) - only show if not default */}
                              {listing.pricing?.price_unit && (
                                <span className="text-muted-foreground text-sm font-normal ml-0.5">
                                  /{listing.pricing.price_unit}
                                </span>
                              )}
                              {listing.listing_type === 'rent' && (
                                <span className="text-muted-foreground text-sm font-normal ml-1">
                                  / {getRentalPeriodLabel(listing.pricing?.rental_period || (listing.pricing?.is_hourly ? 'hourly' : 'monthly'), language)}
                                </span>
                              )}
                              {listing.listing_type === 'yard_sale' && listing.yard_sale?.items?.length > 0 && (
                                <span className="text-muted-foreground text-sm font-normal ml-1">
                                  ({listing.yard_sale.items.length} {language === 'nl' ? 'artikelen' : 'items'})
                                </span>
                              )}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                listing.status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                                listing.status === 'sold' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                listing.status === 'reserved' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                listing.status === 'expired' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                listing.status === 'archived' ? 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' :
                                'bg-muted text-muted-foreground'
                              }`}>
                                {listing.status === 'sold' ? t('sold') : 
                                 listing.status === 'reserved' ? t('reserved') : 
                                 listing.status === 'expired' ? t('expired') : 
                                 listing.status === 'archived' ? t('archived') :
                                 listing.status === 'active' ? t('active') : listing.status}
                              </span>
                              <span className="flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                {listing.views}
                              </span>
                            </div>
                            {isPromoted && (
                              <p className="text-xs text-purple-600 dark:text-purple-400 mt-1 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {promotionTimeLeft}
                              </p>
                            )}
                            {/* Expiry info for active listings */}
                            {listing.status === 'active' && listing.days_remaining !== undefined && (
                              <p className={`text-xs mt-1 flex items-center gap-1 ${
                                listing.days_remaining <= 5 
                                  ? 'text-amber-600 dark:text-amber-400' 
                                  : 'text-muted-foreground'
                              }`}>
                                <Clock className="w-3 h-3" />
                                {listing.days_remaining} {t('days_remaining')}
                                {listing.extension_count > 0 && (
                                  <span className="ml-1 px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded text-[10px] font-medium">
                                    {listing.extension_count}x {t('times_extended').replace('times extended', '').replace('keer verlengd', '')}
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                          {/* Single Manage Button */}
                          <button
                            onClick={() => setManageListing(listing)}
                            className="p-2.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl transition-colors"
                            data-testid={`manage-listing-mobile-${listing.id}`}
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      {/* Desktop Layout */}
                      <div className="hidden md:grid md:grid-cols-12 gap-4 items-center">
                        <div className="col-span-4 flex items-center gap-4">
                          <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                            {listing.images?.[0] ? (
                              <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-6 h-6 text-muted-foreground" />
                              </div>
                            )}
                            {isPromoted && (
                              <div className="absolute top-1 left-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] px-1 py-0.5 rounded-full">
                                <Zap className="w-2.5 h-2.5" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <Link to={`/listing/${listing.id}`} className="font-medium text-foreground hover:text-primary line-clamp-1">
                              {listing.title}
                            </Link>
                            <p className="text-sm text-muted-foreground mt-0.5">{formatDate(listing.created_at)}</p>
                            {isPromoted && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {promotionTimeLeft}
                              </p>
                            )}
                            {/* Expiry info for active listings */}
                            {listing.status === 'active' && listing.days_remaining !== undefined && (
                              <p className={`text-xs flex items-center gap-1 ${
                                listing.days_remaining <= 5 
                                  ? 'text-amber-600 dark:text-amber-400' 
                                  : 'text-muted-foreground'
                              }`}>
                                <Clock className="w-3 h-3" />
                                {listing.days_remaining}d
                                {listing.extension_count > 0 && (
                                  <span className="ml-1 px-1 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded text-[10px]">
                                    {listing.extension_count}x
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <span className="font-semibold text-primary">
                            {formatPrice(listing.pricing?.amount || 0, listing.pricing?.currency)}
                          </span>
                          {/* Price unit suffix (e.g., /m²) */}
                          {listing.pricing?.price_unit && (
                            <span className="text-muted-foreground text-sm ml-0.5">
                              /{listing.pricing.price_unit}
                            </span>
                          )}
                          {listing.listing_type === 'rent' && (
                            <span className="text-muted-foreground text-sm">
                              / {getRentalPeriodLabel(listing.pricing?.rental_period || (listing.pricing?.is_hourly ? 'hourly' : 'monthly'), language)}
                            </span>
                          )}
                        </div>
                        <div className="col-span-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            listing.status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 
                            listing.status === 'expired' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                            listing.status === 'archived' ? 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' :
                            listing.status === 'sold' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {listing.status === 'expired' ? t('expired') : 
                             listing.status === 'archived' ? t('archived') :
                             listing.status === 'sold' ? t('sold') :
                             listing.status === 'active' ? t('active') : listing.status}
                          </span>
                        </div>
                        <div className="col-span-2 flex items-center gap-1 text-muted-foreground">
                          <Eye className="w-4 h-4" />
                          {listing.views}
                        </div>
                        <div className="col-span-2 flex items-center justify-end gap-2">
                          {/* Single Manage Button for Desktop */}
                          <button
                            onClick={() => setManageListing(listing)}
                            className="flex items-center gap-2 px-3 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl transition-colors text-sm font-medium"
                            data-testid={`manage-listing-${listing.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                            {language === 'nl' ? 'Beheer' : 'Manage'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="pagination mt-8">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="page-btn"
                >
                  &larr;
                </button>
                {[...Array(pages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setPage(i + 1)}
                    className={`page-btn ${page === i + 1 ? 'active' : ''}`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  className="page-btn"
                >
                  &rarr;
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state bg-card rounded-2xl py-16 border border-border">
            <Package className="empty-icon" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No listings yet</h3>
            <p className="text-muted-foreground mb-6">Create your first listing and start selling!</p>
            <Link
              to="/create-listing"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-medium hover:opacity-90 transition-opacity btn-press"
            >
              <Plus className="w-5 h-5" />
              {t('create_listing')}
            </Link>
          </div>
        )}
      </DashboardContainer>

      {/* Promotion Modal - Coming Soon with Sentoo branding */}
      {showPromotionModal && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => !promoting && setShowPromotionModal(null)}>
          <div 
            className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()} 
            data-testid="promotion-modal"
          >
            {/* Coming Soon Overlay */}
            <ComingSoonOverlay
              title={tb('coming_soon')}
              subtitle={tb('sentoo_integration')}
              icon={Zap}
              useBoostEnabled={true}
            >
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Zap className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{tb('boost_your_listing')}</h3>
                    <p className="text-white/80 text-sm">{tb('get_more_visibility')}</p>
                  </div>
                </div>
              </div>

              {/* Package options - Use boost packages from config */}
              <div className="p-6 space-y-3">
                {(boostConfig.packages || []).map((pkg) => (
                  <button
                    key={pkg.id}
                    onClick={() => handleBoost(showPromotionModal, pkg.id)}
                    disabled={promoting}
                    className="w-full p-4 rounded-xl border border-border bg-card hover:border-amber-400/50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`boost-package-${pkg.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-foreground">
                          {pkg.names?.[language] || pkg.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {pkg.descriptions?.[language] || pkg.description}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-amber-500">XCG {pkg.price.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">
                          {pkg.duration_hours < 48 
                            ? `${pkg.duration_hours} ${tb('hours')}` 
                            : `${Math.round(pkg.duration_hours / 24)} ${tb('days')}`}
                        </p>
                      </div>
                    </div>
                    {promoting && (
                      <div className="mt-2 flex items-center justify-center">
                        <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                      </div>
                    )}
                  </button>
                ))}

                {/* Sentoo Trust Badge */}
                <div className="mt-4 p-3 bg-muted/30 rounded-xl">
                  <div className="flex items-center justify-center">
                    <img 
                      src={SENTOO_LOGO_BADGE} 
                      alt="Pay securely with Sentoo" 
                      className="h-16 object-contain"
                    />
                  </div>
                </div>

                {/* Cancel button */}
                <button
                  onClick={() => setShowPromotionModal(null)}
                  disabled={promoting}
                  className="w-full py-3 mt-2 border border-border text-muted-foreground rounded-xl font-medium hover:bg-muted transition-colors"
                >
                  {tb('cancel')}
                </button>
              </div>
            </ComingSoonOverlay>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteId && (
        <div className="modal-overlay" onClick={() => !deleting && setDeleteId(null)}>
          <div className="modal-content p-6" onClick={(e) => e.stopPropagation()} data-testid="delete-modal">
            <h3 className="text-xl font-semibold text-foreground mb-2">Delete Listing</h3>
            <p className="text-muted-foreground mb-6">Are you sure you want to delete this listing? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                disabled={deleting}
                className="flex-1 py-2.5 border border-border text-muted-foreground rounded-full font-medium hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-full font-medium hover:bg-red-600 transition-colors disabled:opacity-50"
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Listing Modal */}
      {manageListing && (
        <div className="modal-overlay" onClick={() => !markingStatus && setManageListing(null)}>
          <div className="modal-content p-5 max-w-sm !bg-background text-foreground rounded-2xl border border-border/50 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="manage-listing-modal">
            {/* Header with listing info */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                {manageListing.images?.[0] ? (
                  <img src={manageListing.images[0]} alt={manageListing.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-foreground line-clamp-1 text-sm">{manageListing.title}</h3>
                <span className={`text-xs font-medium ${
                  manageListing.status === 'active' ? 'text-emerald-600 dark:text-emerald-400' :
                  manageListing.status === 'reserved' ? 'text-orange-600 dark:text-orange-400' :
                  manageListing.status === 'sold' ? 'text-red-600 dark:text-red-400' :
                  'text-muted-foreground'
                }`}>
                  {manageListing.status === 'active' ? (language === 'nl' ? 'Actief' : 'Active') :
                   manageListing.status === 'reserved' ? (language === 'nl' ? 'Gereserveerd' : 'Reserved') :
                   manageListing.status === 'sold' ? (language === 'nl' ? 'Verkocht' : 'Sold') :
                   manageListing.status}
                </span>
              </div>
              <button
                onClick={() => setManageListing(null)}
                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Status Actions - Primary */}
            {(manageListing.status === 'active' || manageListing.status === 'reserved' || manageListing.status === 'sold' || manageListing.status === 'expired' || manageListing.status === 'under_offer') && (
              <div className="mb-5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  {language === 'nl' ? 'Wijzig Status' : 'Change Status'}
                </p>
                
                {/* Active listing: Grid with Sold, Reserved, Under Offer & Reduce Price */}
                {manageListing.status === 'active' && (
                  <div className="grid grid-cols-2 gap-2">
                    {/* Sold - Primary action, prominent */}
                    <button
                      onClick={() => handleMarkSold(manageListing.id)}
                      disabled={markingStatus?.id === manageListing.id}
                      className="flex flex-col items-center gap-1.5 p-3 bg-zinc-900 dark:bg-zinc-100 text-zinc-50 dark:text-zinc-900 rounded-xl font-medium transition-colors disabled:opacity-50 shadow-sm hover:opacity-90"
                      data-testid="modal-mark-sold"
                    >
                      {markingStatus?.id === manageListing.id && markingStatus?.action === 'sold' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Check className="w-5 h-5" />
                      )}
                      <span className="text-xs">{language === 'nl' ? 'Verkocht' : 'Sold'}</span>
                    </button>
                    {/* Reserved - Secondary, neutral with orange icon */}
                    <button
                      onClick={() => handleMarkReserved(manageListing.id)}
                      disabled={markingStatus?.id === manageListing.id}
                      className="flex flex-col items-center gap-1.5 p-3 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-medium transition-colors disabled:opacity-50"
                      data-testid="modal-mark-reserved"
                    >
                      {markingStatus?.id === manageListing.id && markingStatus?.action === 'reserved' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Clock className="w-5 h-5 text-orange-500" />
                      )}
                      <span className="text-xs">{language === 'nl' ? 'Gereserveerd' : 'Reserved'}</span>
                    </button>
                    {/* Under Offer - Secondary, neutral with amber icon */}
                    <button
                      onClick={() => handleMarkUnderOffer(manageListing.id)}
                      disabled={markingStatus?.id === manageListing.id}
                      className="flex flex-col items-center gap-1.5 p-3 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-medium transition-colors disabled:opacity-50"
                      data-testid="modal-mark-under-offer"
                    >
                      {markingStatus?.id === manageListing.id && markingStatus?.action === 'under_offer' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <FileCheck className="w-5 h-5 text-amber-500" />
                      )}
                      <span className="text-xs">{language === 'nl' ? 'Onder Bod' : 'Under Offer'}</span>
                    </button>
                    {/* Reduce Price - Functional, neutral with green icon */}
                    <button
                      onClick={() => handleOpenPriceReduction(manageListing)}
                      disabled={markingStatus?.id === manageListing.id}
                      className="flex flex-col items-center gap-1.5 p-3 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-medium transition-colors disabled:opacity-50"
                      data-testid="modal-reduce-price"
                    >
                      <TrendingDown className="w-5 h-5 text-emerald-500" />
                      <span className="text-xs">{language === 'nl' ? 'Prijs Verlagen' : 'Reduce Price'}</span>
                    </button>
                  </div>
                )}
                
                {/* Reserved listing: Grid with Sold & Remove reservation */}
                {manageListing.status === 'reserved' && (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleMarkSold(manageListing.id)}
                      disabled={markingStatus?.id === manageListing.id}
                      className="flex flex-col items-center gap-1.5 p-3 bg-zinc-900 dark:bg-zinc-100 text-zinc-50 dark:text-zinc-900 rounded-xl font-medium transition-colors disabled:opacity-50 shadow-sm hover:opacity-90"
                      data-testid="modal-mark-sold"
                    >
                      {markingStatus?.id === manageListing.id && markingStatus?.action === 'sold' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Check className="w-5 h-5" />
                      )}
                      <span className="text-xs">{language === 'nl' ? 'Verkocht' : 'Sold'}</span>
                    </button>
                    <button
                      onClick={() => handleUnmarkReserved(manageListing.id)}
                      disabled={markingStatus?.id === manageListing.id}
                      className="flex flex-col items-center gap-1.5 p-3 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-medium transition-colors disabled:opacity-50"
                      data-testid="modal-remove-reserved"
                    >
                      {markingStatus?.id === manageListing.id && markingStatus?.action === 'unreserved' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground" />
                      )}
                      <span className="text-xs text-center leading-tight">{language === 'nl' ? 'Verwijder Reservering' : 'Remove Reservation'}</span>
                    </button>
                  </div>
                )}
                
                {/* Under Offer listing: Grid with Sold & Remove under offer */}
                {manageListing.status === 'under_offer' && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => handleMarkSold(manageListing.id)}
                        disabled={markingStatus?.id === manageListing.id}
                        className="flex flex-col items-center gap-1.5 p-3 bg-zinc-900 dark:bg-zinc-100 text-zinc-50 dark:text-zinc-900 rounded-xl font-medium transition-colors disabled:opacity-50 shadow-sm hover:opacity-90"
                        data-testid="modal-mark-sold"
                      >
                        {markingStatus?.id === manageListing.id && markingStatus?.action === 'sold' ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Check className="w-5 h-5" />
                        )}
                        <span className="text-xs">{language === 'nl' ? 'Verkocht' : 'Sold'}</span>
                      </button>
                      <button
                        onClick={() => handleUnmarkUnderOffer(manageListing.id)}
                        disabled={markingStatus?.id === manageListing.id}
                        className="flex flex-col items-center gap-1.5 p-3 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-medium transition-colors disabled:opacity-50"
                        data-testid="modal-remove-under-offer"
                      >
                        {markingStatus?.id === manageListing.id && markingStatus?.action === 'un_under_offer' ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <X className="w-5 h-5 text-muted-foreground" />
                        )}
                        <span className="text-xs text-center leading-tight">{language === 'nl' ? 'Bod Verwijderen' : 'Remove Offer'}</span>
                      </button>
                    </div>
                    {/* Still allow price reduction while under offer */}
                    <button
                      onClick={() => handleOpenPriceReduction(manageListing)}
                      disabled={markingStatus?.id === manageListing.id}
                      className="w-full flex flex-col items-center gap-1.5 p-3 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-medium transition-colors disabled:opacity-50"
                      data-testid="modal-reduce-price-under-offer"
                    >
                      <TrendingDown className="w-5 h-5 text-emerald-500" />
                      <span className="text-xs">{language === 'nl' ? 'Prijs Verlagen' : 'Reduce Price'}</span>
                    </button>
                  </div>
                )}
                
                {/* Sold listing: Single reactivate button */}
                {manageListing.status === 'sold' && (
                  <button
                    onClick={() => handleUnmarkSold(manageListing.id)}
                    disabled={markingStatus?.id === manageListing.id}
                    className="w-full flex items-center justify-center gap-2 p-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                    data-testid="modal-reactivate"
                  >
                    {markingStatus?.id === manageListing.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-5 h-5" />
                    )}
                    <span className="text-sm">{language === 'nl' ? 'Heractiveren' : 'Reactivate'}</span>
                  </button>
                )}

                {/* Expired listing: Single reactivate button */}
                {manageListing.status === 'expired' && (
                  <button
                    onClick={() => {
                      handleExtendListing(manageListing.id);
                      setManageListing(null);
                    }}
                    disabled={extendingId === manageListing.id}
                    className="w-full flex items-center justify-center gap-2 p-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                  >
                    {extendingId === manageListing.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-5 h-5" />
                    )}
                    <span className="text-sm">{language === 'nl' ? 'Heractiveren' : 'Reactivate'}</span>
                  </button>
                )}
              </div>
            )}

            {/* Management Actions - Secondary (subtle styling) */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                {language === 'nl' ? 'Beheer' : 'Manage'}
              </p>
              <div className="space-y-1.5">
                {/* Boost - only for active non-promoted listings */}
                {manageListing.status === 'active' && !manageListing.promoted_until && (
                  <button
                    onClick={() => {
                      setShowPromotionModal(manageListing.id);
                      setManageListing(null);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 bg-secondary/40 hover:bg-secondary text-foreground rounded-lg border border-border/50 transition-colors text-sm font-medium"
                    data-testid="modal-boost"
                  >
                    <Zap className="w-4 h-4 text-amber-500" />
                    {language === 'nl' ? 'Boost advertentie' : 'Boost listing'}
                  </button>
                )}
                
                {/* Edit */}
                <Link
                  to={`/edit-listing/${manageListing.id}`}
                  className="w-full flex items-center gap-3 px-3 py-2.5 bg-secondary/40 hover:bg-secondary text-foreground rounded-lg border border-border/50 transition-colors text-sm font-medium"
                  data-testid="modal-edit"
                >
                  <Edit2 className="w-4 h-4 text-muted-foreground" />
                  {language === 'nl' ? 'Bewerken' : 'Edit'}
                </Link>
                
                {/* Delete */}
                <button
                  onClick={() => {
                    setDeleteId(manageListing.id);
                    setManageListing(null);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 bg-red-500/5 hover:bg-red-50 dark:bg-red-500/10 dark:hover:bg-red-500/20 text-red-500 rounded-lg border border-red-500/20 transition-colors text-sm font-medium"
                  data-testid="modal-delete"
                >
                  <Trash2 className="w-4 h-4" />
                  {language === 'nl' ? 'Verwijderen' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Price Reduction Modal */}
      <PriceReductionModal
        isOpen={showPriceReductionModal}
        onClose={() => {
          setShowPriceReductionModal(false);
          setPriceReductionListing(null);
        }}
        onConfirm={handleConfirmPriceReduction}
        currentPrice={priceReductionListing?.pricing?.amount || 0}
        originalPrice={priceReductionListing?.pricing?.original_price}
        currency={priceReductionListing?.pricing?.currency || 'USD'}
        isLoading={reducingPrice}
      />

      <Footer />
    </div>
  );
};

export default MyListingsPage;
